import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import userService, { PanelTeamTicket } from '../../../shared/services/userService';
import { isPanelTeamMember } from '../panelAccess/panelRoleAccess';

const TeamSupport: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isEmbedded = embedded || new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('embed') === '1';
  const { t } = usePageTranslation('panel', 'TeamSupport');

  const isPanelMember = currentUser?.roles_id != null && isPanelTeamMember(currentUser.roles_id);
  const isAdmin = currentUser?.roles_id != null && currentUser.roles_id <= 7;

  const [accessChecked, setAccessChecked] = useState(false);
  /** Référent actif (au moins une fonction) — requis pour ouvrir un ticket */
  const [isReferent, setIsReferent] = useState(false);

  const [tickets, setTickets] = useState<PanelTeamTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<'demande' | 'signalement' | 'question'>('demande');

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await userService.getPanelTeamTickets();
      if (res.success) setTickets(res.data);
    } catch (e: any) {
      setError(e.message || t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentUser) {
        navigate('/');
        return;
      }
      if (!isPanelMember) {
        navigate('/panel');
        return;
      }
      let referentOk = false;
      try {
        const res = await userService.getMyReferents();
        if (!cancelled && res.success && res.data?.length) {
          referentOk = true;
          setIsReferent(true);
        }
      } catch {
        if (!cancelled) setIsReferent(false);
      }
      const canView = isAdmin || referentOk;
      if (!cancelled && !canView) {
        navigate('/panel');
        return;
      }
      if (!cancelled) {
        setAccessChecked(true);
        await load();
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isPanelMember, isAdmin, navigate, load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await userService.createPanelTeamTicket({
        title: title.trim(),
        body: body.trim(),
        category,
      });
      setTitle('');
      setBody('');
      setCategory('demande');
      await load();
    } catch (e: any) {
      setError(e.message || t('createError'));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: number, status: 'open' | 'in_progress' | 'closed') => {
    setSaving(true);
    setError(null);
    try {
      await userService.updatePanelTeamTicketStatus(id, status);
      await load();
    } catch (e: any) {
      setError(e.message || t('updateError'));
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = (s: string) => {
    const key = `status.${s}` as const;
    const v = t(key);
    return v === key ? s : v;
  };

  if (!accessChecked) {
    return isEmbedded ? (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    ) : (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <BackgroundImage />
        <ParticlesBackground />
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={!isEmbedded ? 'min-h-screen relative overflow-hidden' : ''}>
      {!isEmbedded && <BackgroundImage />}
      {!isEmbedded && <ParticlesBackground />}
      {!isEmbedded && <Navbar />}
      <div className={!isEmbedded ? 'relative z-10 pt-24 pb-16 px-4 max-w-5xl mx-auto' : ''}>
        {!isEmbedded && (
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-cockpit-text mb-2">{t('title')}</h1>
            <p className="text-cockpit-text-2">{t('subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/panel')}
            className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-cockpit-text text-sm shrink-0"
          >
            {t('back')}
          </button>
        </div>
        )}

        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/15 border border-red-500/40 text-red-200 text-sm">{error}</div>
        )}

        <div className={`grid gap-8 ${isReferent ? 'lg:grid-cols-2' : ''}`}>
          {isReferent && (
            <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 p-5 backdrop-blur-sm h-fit">
              <h2 className="text-sm font-semibold text-cockpit-text mb-4">{t('formTitle')}</h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-cockpit-text-2 mb-1.5">{t('fieldTitle')}</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('fieldTitlePh')}
                    maxLength={200}
                    className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-cockpit-text-2 mb-1.5">{t('fieldCategory')}</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as typeof category)}
                    className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text focus:outline-none focus:border-[#6366f1]/60"
                  >
                    <option value="demande">{t('catDemande')}</option>
                    <option value="signalement">{t('catSignalement')}</option>
                    <option value="question">{t('catQuestion')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-cockpit-text-2 mb-1.5">{t('fieldBody')}</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={t('fieldBodyPh')}
                    rows={6}
                    maxLength={8000}
                    className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60 resize-y min-h-[120px]"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving || !title.trim() || !body.trim()}
                  className="w-full rounded-lg border border-amber-500/40 bg-amber-500/15 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40 transition-colors"
                >
                  {saving ? t('submitting') : t('submit')}
                </button>
              </form>
              <p className="mt-4 text-xs text-cockpit-muted">
                {isAdmin ? t('adminHint') : t('authorCloseHint')}
              </p>
            </div>
          )}

          {!isReferent && isAdmin && (
            <div className="lg:col-span-2 mb-2 rounded-lg border border-[#223046] bg-[#0b1220]/80 px-4 py-3 text-sm text-cockpit-text-2">
              {t('referentsOnlyForm')}
            </div>
          )}

          <div className={`rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 p-5 backdrop-blur-sm ${!isReferent ? 'lg:col-span-2' : ''}`}>
            <h2 className="text-sm font-semibold text-cockpit-text mb-4">{t('listTitle')}</h2>
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : tickets.length === 0 ? (
              <p className="text-cockpit-text-2 text-sm">{t('empty')}</p>
            ) : (
              <ul className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                {tickets.map((tk) => {
                  const mine = tk.user_id === currentUser?.id;
                  return (
                    <li
                      key={tk.id}
                      className="rounded-lg border border-[#223046] bg-[#0b1220]/80 p-4 text-sm"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <span className="font-semibold text-cockpit-text">{tk.title}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded border ${
                            tk.status === 'closed'
                              ? 'border-slate-500/50 text-cockpit-text-2'
                              : tk.status === 'in_progress'
                                ? 'border-[#6366f1]/50 text-[#c7d2fe]'
                                : 'border-amber-500/50 text-amber-200'
                          }`}
                        >
                          {statusLabel(tk.status)}
                        </span>
                      </div>
                      <p className="text-cockpit-text-2 whitespace-pre-wrap mb-2 text-xs">{tk.body}</p>
                      <p className="text-xs text-cockpit-muted mb-3">
                        {t('by')} <span className="text-cockpit-text-2">{tk.author_username}</span> · {t('date')}{' '}
                        {new Date(tk.created_at).toLocaleString()}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {isAdmin && tk.status === 'open' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'in_progress')}
                            className="rounded border border-[#6366f1]/40 bg-[#6366f1]/20 px-3 py-1 text-xs font-medium text-[#c7d2fe] hover:bg-[#6366f1]/30 disabled:opacity-40 transition-colors"
                          >
                            {t('actions.take')}
                          </button>
                        )}
                        {isAdmin && tk.status === 'in_progress' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'closed')}
                            className="rounded border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1 text-xs font-medium text-cockpit-text hover:bg-cockpit-bg-2/45 disabled:opacity-40 transition-colors"
                          >
                            {t('actions.close')}
                          </button>
                        )}
                        {isAdmin && tk.status === 'closed' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'open')}
                            className="rounded border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1 text-xs font-medium text-cockpit-text hover:bg-cockpit-bg-2/45 disabled:opacity-40 transition-colors"
                          >
                            {t('actions.reopen')}
                          </button>
                        )}
                        {!isAdmin && mine && tk.status !== 'closed' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setStatus(tk.id, 'closed')}
                            className="rounded border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1 text-xs font-medium text-cockpit-text hover:bg-cockpit-bg-2/45 disabled:opacity-40 transition-colors"
                          >
                            {t('actions.close')}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamSupport;
